/**
 * ModulesContext — список `super_admin_modules` и их `enabled`-флаги
 * (FE-11, US-09).
 *
 * - Super-admin: фетчим `/api/super-admin/modules` (полный список с
 *   label'ами); toggle через `setEnabled` (PUT BE-11).
 * - Остальные авторизованные пользователи: фетчим read-only
 *   `/api/public/modules` (US-09 FR-1.4) — выключенное супер-админом
 *   скрывается у всех пользователей области, а не только в его UI.
 * - Аноним / до загрузки userinfo: пустой `Map`, `useModulePresent`
 *   возвращает `true` (default-visible).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { useUser } from './UserContext';
import { listPublicModules } from '../requests/public.request';
import {
  listModules,
  setModuleEnabled as setModuleEnabledRequest,
  type ModuleInfo,
} from '../requests/super_admin.request';

interface ModulesContextValue {
  /** Map<moduleId, ModuleInfo>. Пустой для анонима и при ошибке загрузки. */
  modules: ReadonlyMap<string, ModuleInfo>;
  loading: boolean;
  /**
   * Последний фетч упал (сеть/403/5xx). Пустой `modules` при `error` —
   * «не смогли загрузить», а не «модулей нет»: admin-UI показывает ошибку,
   * а не «Нет данных»; для витрины разницы нет — default-visible.
   */
  error: boolean;
  /** Toggle для super-admin (PUT BE-11). Возвращает обновлённый объект. */
  setEnabled: (moduleId: string, enabled: boolean) => Promise<void>;
}

const EMPTY_MAP: ReadonlyMap<string, ModuleInfo> = new Map();

/** Внутренний context — экспортируется для useModulePresent. */
export const ModulesContext = createContext<ModulesContextValue | null>(null);

export const ModulesProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const [modules, setModules] = useState<ReadonlyMap<string, ModuleInfo>>(EMPTY_MAP);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isAdmin = user?.is_admin ?? false;
  const userId = user?.user_id ?? null;

  useEffect(() => {
    if (userId === null) {
      setModules(EMPTY_MAP);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Не-админу label'ы не нужны — только enabled-флаги для useModulePresent.
    const fetchList: Promise<ModuleInfo[]> = isAdmin
      ? listModules()
      : listPublicModules().then((list) =>
          list.map((m) => ({ ...m, label_ru: null, label_kz: null })),
        );
    fetchList
      .then((list) => {
        if (cancelled) return;
        const map = new Map(list.map((m) => [m.module_id, m]));
        setModules(map);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setModules(EMPTY_MAP);
        setError(true);
        console.error('modules fetch failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // userId в зависимостях: смена учётки без смены is_admin тоже перезагружает флаги.
  }, [isAdmin, userId]);

  const setEnabled = useCallback(
    async (moduleId: string, enabled: boolean) => {
      const updated = await setModuleEnabledRequest(moduleId, enabled);
      setModules((current) => {
        const next = new Map(current);
        next.set(moduleId, updated);
        return next;
      });
    },
    [],
  );

  return (
    <ModulesContext.Provider value={{ modules, loading, error, setEnabled }}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = (): ModulesContextValue => {
  const ctx = useContext(ModulesContext);
  if (!ctx) {
    throw new Error('useModules must be used inside <ModulesProvider>');
  }
  return ctx;
};
